import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
} from "../../../Common/UI/exports";
import { updateUserRoles } from "../../../APIs/userManagementAPI";
import { fetchRoles } from "../../../APIs/roleManagementAPI";
import { toast } from "react-toastify";

function EditUser() {
  const { state } = useLocation();
  const user = state?.user; // { id, username, roles?: string[] }

  // All roles (objects or strings)
  const [roles, setRoles] = useState([]);

  // Assigned roles as a Set of names for fast toggle
  const [assignedSet, setAssignedSet] = useState(
    new Set(Array.isArray(user?.roles) ? user.roles : [])
  );

  const [initialRoles, setInitialRoles] = useState(
    new Set(Array.isArray(user?.roles) ? user.roles : [])
  );

  const [saving, setSaving] = useState(false);

  // Load all roles (left table)
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const all = await fetchRoles();
        setRoles(Array.isArray(all) ? all : []);
      } catch (error) {
        console.error("Error loading Roles:", error);
        toast.error("Failed to load Roles.");
      }
    };
    loadRoles();
  }, []);

  // Derive assigned list (right table) from roles + assignedSet
  const assignedList = useMemo(() => {
    // Normalize to role names
    const getName = (r) => (typeof r === "string" ? r : r?.name);
    return roles
      .map(getName)
      .filter((name) => !!name && assignedSet.has(name));
  }, [roles, assignedSet]);


  // Toggle checkbox (assign/unassign)
  const toggleAssign = (roleName) => {
    setAssignedSet((prev) => {
      const next = new Set(prev);
      next.has(roleName) ? next.delete(roleName) : next.add(roleName);
      return next;
    });
  };

  const handleCancel = () => {
    if (!hasChanges) return;

    if (window.confirm("Discard unsaved changes?")) {
      toast.info("Changes discarded");
      setAssignedSet(new Set(initialRoles));
    }
  };


  const handleSave = async () => {
    try {
      setSaving(true);

      const updatedRolesToSave = Array.from(assignedSet);

      await updateUserRoles(user.id, updatedRolesToSave);

      await new Promise(resolve => setTimeout(resolve, 300));

      setInitialRoles(new Set(updatedRolesToSave));

      toast.success("User roles updated successfully!");
    } catch (error) {
      console.error("Failed to update roles:", error);
      toast.error("Failed to save user roles.");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    initialRoles.size !== assignedSet.size ||
    [...assignedSet].some(r => !initialRoles.has(r));


  // Helper to extract name from role item
  const roleNameOf = (role) => (typeof role === "string" ? role : role?.name);

  return (
    <BasePageLayout sidebarItems={[]}>
      <ToastContainer position="top-right" autoClose={3000} />
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)",
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseBox
          sx={{
            mb: 2,
            p: 1.5,
            borderLeft: "4px solid #eb0d24",
            backgroundColor: "#fafafa",
          }}
        >
          <BaseTypography sx={{ fontWeight: 600 }}>
            User: {user?.fullName || user?.username || "Unknown"}
          </BaseTypography>

          <BaseTypography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
            Assign or remove roles for this user
          </BaseTypography>
        </BaseBox>


        <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <BaseBox
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {/* Left: All Roles with checkboxes */}
            <BaseBox
              sx={{
                flex: "1 1 360px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                alignSelf: "flex-start",
                maxHeight: "400px",
                overflowY: "auto",
              }}

            >
              <table className="roles-table">
                <thead>
                  <tr>
                    <th>All Roles (check to assign)</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length ? (
                    roles.map((role) => {
                      const name = roleNameOf(role);
                      if (!name) return null;
                      const checked = assignedSet.has(name);
                      return (
                        <tr key={name}>
                          <td title={name}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAssign(name)}
                                style={{ marginRight: 8 }}
                              />
                              {name}
                            </label>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="empty">No roles.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </BaseBox>

            {/* Right: Assigned Roles (derived, read-only) */}
            <BaseBox
              sx={{
                flex: "1 1 360px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                alignSelf: "flex-start",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <table className="roles-table assigned-roles">
                <thead>
                  <tr>
                    <th>Assigned Roles</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedList.length ? (
                    assignedList.map((name) => (
                      <tr key={name}>
                        <td title={name}>{name}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="empty">No assigned roles.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </BaseBox>
          </BaseBox>
          <BaseBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
              flexWrap: "wrap",
            }}
          >

            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: hasChanges ? "#eb0d24" : "#ccc",
                color: "#fff",
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving || !hasChanges}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: hasChanges ? "#eb0d24" : "#ccc",
                color: "#fff",
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: !hasChanges ? 0.7 : 1,
              }}
            >
              Cancel
            </button>
          </BaseBox>
        </BaseBox>
      </BaseBox>
    </BasePageLayout>
  );
}

export default EditUser;